import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  ValidationResolver,
  ValidationResolverContext,
  ValidationResolutionResult,
} from '../../models/kyc-validation.model';
import { LegalNameFixModalComponent, LegalNameFixDialogData } from 'src/app/shared/components/simple/legal-name-fix-modal/legal-name-fix-modal.component';
import { confirmAdoptSerproName } from '../../utils/kyc-name-fix.util';
import { KycService } from '../kyc.service';
import { KycMismatchAction } from '../../models/kyc-mismatch.model';

/**
 * Resolver para problemas de incompatibilidade de nome legal
 *
 * Quando o nome no documento (Veriff) não bate com o nome legal no sistema,
 * abre um modal para o usuário corrigir o nome.
 *
 * ACTIONS:
 * - MATCH: Auto-aceita (diferença mínima)
 * - ACCEPT_CHANGES: Modal com diff, aceitar mudança
 * - FIX_CHANGES: Modal com input blind para digitar nome
 * - VERIFY_CHANGES: Modal com opções A/B/C
 * - REUPLOAD: Bloqueia e pede novo documento
 * - MANUAL_REVIEW: Deixa pendente para revisão
 * - DATA_MISSING: Pede retry
 */
export class LegalNameMismatchResolver implements ValidationResolver {
  validationType = 'legal_name_match';

  constructor(
    private dialog: MatDialog,
    private kycService: KycService,
    private translate: TranslateService
  ) {}

  canResolve(context: ValidationResolverContext): boolean {
    // [SECURITY] Se o check foi cancelado por falha de cross-validation de
    // identidade, NÃO abrir o modal de name mismatch — o usuário deve iniciar
    // um novo KYC do zero.
    const check = (context as any).kycCheck;
    const pr = check?.provider_return;
    const identityFraud =
      pr?.identity_cross_validation_failed === true ||
      pr?.serpro_cross_validation_failed === true;
    if (check?.stage === 'CANCELLED' || identityFraud) {
      console.warn('[Resolver] Skipping name mismatch modal — check is cancelled or has identity fraud flag', {
        stage: check?.stage,
        identityFraud,
      });
      return false;
    }
    return (
      context.validation.validation === this.validationType &&
      !!(context as any).dialog // Requer MatDialog no contexto
    );
  }

  async resolve(
    context: ValidationResolverContext
  ): Promise<ValidationResolutionResult> {
    console.log('[Resolver] Resolving legal_name_match...');
    console.log('[Resolver] Details:', context.validation.details);

    // 🔍 NAMES_MISMATCH: Obter classificação do mismatch usando KycService
    const mismatchAction = this.kycService.getNamesMismatchAction(context.kycCheck);
    const mismatchData = this.kycService.getNamesMismatchFromNotes(context.kycCheck);

    console.log('[Resolver] Mismatch classification:', {
      action: mismatchAction,
      hasData: !!mismatchData,
      isBlocked: this.kycService.isNamesMismatchBlocked(context.kycCheck)
    });

    // 🔍 NAMES_MISMATCH: Decidir comportamento baseado na action
    if (mismatchAction === 'REUPLOAD') {
      // Diferença grande - bloqueia e pede novo documento
      return {
        resolved: false,
        requiresNewKyc: true,
        message: this.kycService.getNamesMismatchMessage(mismatchAction, 'pt'),
        newState: 'BLOCKED'
      };
    }

    if (mismatchAction === 'REVIEW_AND_RETRY') {
      // somoskyc_v1: nome não é similar ao registro do SERPRO. Manda o usuário
      // revisar os dados (nome/CPF/DOB) e refazer o KYC — tentativa falha,
      // Postpone aplica normalmente.
      return {
        resolved: false,
        requiresNewKyc: true,
        message: this.kycService.getNamesMismatchMessage(mismatchAction, 'pt'),
        newState: 'BLOCKED',
      };
    }

    if (mismatchAction === 'MANUAL_REVIEW') {
      // Caso ambíguo - não resolve automaticamente
      return {
        resolved: false,
        requiresNewKyc: false,
        message: this.kycService.getNamesMismatchMessage(mismatchAction, 'pt'),
        newState: 'PENDING'
      };
    }

    if (mismatchAction === 'DATA_MISSING') {
      // Dados insuficientes - pede retry
      return {
        resolved: false,
        requiresNewKyc: true,
        message: this.kycService.getNamesMismatchMessage(mismatchAction, 'pt')
      };
    }

    if (mismatchAction === 'MATCH' || mismatchAction === 'AUTO_ACCEPT') {
      // Nomes idênticos após normalização - aceitar automaticamente
      console.log('[Resolver] Auto-accepting: names match after normalization');
      return {
        resolved: true,
        requiresNewKyc: false,
        message: this.kycService.getNamesMismatchMessage(mismatchAction, 'pt')
      };
    }

    // 🇧🇷 SERPRO: CONFIRM_AUTO_ACTION — temos o nome legal oficial (validação de CPF).
    // Mostramos a correção sugerida; ao confirmar, a lambda aplica o nome e revalida.
    if (mismatchAction === 'CONFIRM_AUTO_ACTION') {
      const mismatchData = this.kycService.getNamesMismatchFromNotes(context.kycCheck);
      const suggested = mismatchData?.suggestedCorrection || '';
      console.log('[Resolver] CONFIRM_AUTO_ACTION — suggested:', suggested);

      const confirmed = await confirmAdoptSerproName(
        this.dialog,
        suggested,
        this.translate,
      );
      if (confirmed !== true) {
        // Rejeitou a sugestão → revisar dados e tentar de novo (mesma saída do
        // caso "não similar"): tentativa falha, Postpone aplica normalmente.
        return {
          resolved: false,
          requiresNewKyc: true,
          message: this.kycService.getNamesMismatchMessage('REVIEW_AND_RETRY', 'pt'),
          newState: 'BLOCKED',
        };
      }

      // Confirmado: a lambda aplica o nome do SERPRO e re-roda as validações.
      const revalidation = await this.kycService.revalidateKycCheck(
        context.kycCheck.id || '',
        true
      );

      return {
        resolved: !!revalidation?.validationSummary?.allPassed,
        requiresNewKyc: false,
        message: revalidation?.message || 'Legal name auto-corrected and revalidated',
      };
    }

    // 🔍 NAMES_MISMATCH: Para ACCEPT_CHANGES/FIX_CHANGES/VERIFY_CHANGES, abre modal
    let details = context.validation.details || {};
    const providerReturn = context.kycCheck.provider_return;

    // Se faltar dados em details, tentar extrair de providerReturn ou mismatchData
    if ((!details.veriffDob || !details.veriffIdNumber) && providerReturn) {
      console.log('[Resolver] Details missing, trying to extract from providerReturn');
      const person = providerReturn.verification?.person || providerReturn.document_data_extracted?.person;

      if (person) {
        const fullName = person.firstName + (person.lastName ? ' ' + person.lastName : '');
        details = {
          ...details,
          veriffFullName: details.veriffFullName || fullName,
          veriffDob: details.veriffDob || person.dateOfBirth,
          veriffIdNumber: details.veriffIdNumber || person.idNumber
        };
      }
    }

    // Usar nomes da classificação se disponíveis
    if (mismatchData) {
      details = {
        ...details,
        veriffFullName: details.veriffFullName || mismatchData.veriffName,
        legalName: details.legalName || mismatchData.legalName,
        mismatchAction: mismatchAction,
        mismatchEvidence: mismatchData.evidence,
        suggestedCorrection: mismatchData.suggestedCorrection
      };
    }

    // Determinar actions para o modal (pode ser múltiplas)
    const mismatchActions: KycMismatchAction[] = [];
    if (mismatchAction) {
      mismatchActions.push(mismatchAction);
    }
    // Se precisa de REUPLOAD adicional, adicionar
    if (mismatchData?.actions && Array.isArray(mismatchData.actions)) {
      mismatchData.actions.forEach((a: KycMismatchAction) => {
        if (!mismatchActions.includes(a)) {
          mismatchActions.push(a);
        }
      });
    }

    console.log('[Resolver] Opening modal with actions:', mismatchActions);

    const dialogData: LegalNameFixDialogData = {
      userId: (context as any).userId || context.kycCheck.checked_user_id || '',
      currentLegalName: details.legalName || '',
      veriffName: details.veriffFullName || '',
      kycCheckId: context.kycCheck.id || '',
      doneActionId: (context as any).doneActionId,
      issues: context.validation.reason ? [context.validation.reason] : [],
      documentData: {
        legalName: details.veriffFullName,
        dateOfBirth: details.veriffDob,
        idNumber: details.veriffIdNumber
      },
      // NEW: Pass mismatch classification data to modal
      mismatchAction: mismatchAction || undefined,
      mismatchActions: mismatchActions.length > 0 ? mismatchActions : undefined,
      actionLogic: mismatchData?.actionLogic,
      category: mismatchData?.category,
      subCategory: mismatchData?.subCategory,
      suggestedCorrection: mismatchData?.suggestedCorrection
    };

    const dialogRef = this.dialog.open(LegalNameFixModalComponent, {
      data: dialogData,
      width: '600px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      disableClose: true,
    });

    const result = await dialogRef.afterClosed().toPromise();

    // Handle different modal outcomes
    if (!result) {
      console.log('[Resolver] User closed modal without action');
      return {
        resolved: false,
        requiresNewKyc: false,
        message: 'User closed the dialog',
      };
    }

    if (result.action === 'cancelled') {
      console.log('[Resolver] User cancelled legal name fix');
      return {
        resolved: false,
        requiresNewKyc: false,
        message: 'User cancelled the legal name correction',
      };
    }

    if (result.action === 'reupload' || result.resubmitDocuments) {
      console.log('[Resolver] User requested document reupload');
      return {
        resolved: false,
        requiresNewKyc: true,
        message: 'User needs to resubmit documents',
      };
    }

    if (result.updated) {
      console.log('[Resolver] Legal name updated:', result.newLegalName, 'Action:', result.action);

      // Nome foi atualizado, a lambda já revalidou automaticamente
      return {
        resolved: true,
        requiresNewKyc: false,
        message: result.message || 'Legal name successfully updated and revalidated',
      };
    }

    // Fallback
    return {
      resolved: false,
      requiresNewKyc: false,
      message: 'Unknown result from modal',
    };
  }
}
